import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { X } from "lucide-react";

/**
 * Typeahead autocomplete for foreign states (ISTAT).
 * value = code string | { code, name_it, name_en } | null
 * onChange(newVal) receives { code, name_it, name_en } or null
 */
export default function StatoAutocomplete({ value, onChange, placeholder, testid = "stato-autocomplete" }) {
  const { lang } = useLang();
  const IT = lang === "it";
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!value) { setSelected(null); return; }
    if (typeof value === "object" && value.code) { setSelected(value); return; }
    api.get(`/geo/stati?q=&limit=250`).then((r) => {
      const found = (r.data || []).find((s) => s.code === value);
      if (found) setSelected(found);
    }).catch(() => {});
  }, [value]);

  useEffect(() => {
    if (q.length < 1) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/geo/stati?q=${encodeURIComponent(q)}&lang=${lang}&limit=15`)
        .then((r) => setResults(r.data || []))
        .catch(() => setResults([]));
    }, 200);
    return () => clearTimeout(t);
  }, [q, lang]);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (s) => { setSelected(s); onChange(s); setQ(""); setOpen(false); };
  const clear = () => { setSelected(null); onChange(null); setQ(""); };

  const displayName = (s) => IT ? s.name_it : s.name_en;

  return (
    <div className="relative" ref={ref} data-testid={testid}>
      {selected ? (
        <div className="flex items-center gap-2 border border-primary/40 bg-primary/5 rounded-md px-3 py-1.5 cursor-pointer" onClick={clear}>
          <span className="text-sm font-medium">{displayName(selected)}</span>
          <span className="text-[10px] font-mono text-muted-foreground ml-auto">{selected.code}</span>
          <X className="w-3.5 h-3.5 text-muted-foreground"/>
        </div>
      ) : (
        <Input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder || (IT ? "Cerca stato…" : "Search country…")}
          data-testid={`${testid}-input`}
          autoComplete="off"
        />
      )}
      {open && !selected && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto bg-card border border-border rounded-md shadow-lg" data-testid={`${testid}-results`}>
          {results.map((s) => (
            <button key={s.code} type="button"
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              className="w-full text-left px-3 py-2 hover:bg-primary/5 border-b border-border last:border-b-0 flex items-center gap-2"
              data-testid={`${testid}-opt-${s.code}`}
            >
              <span className="text-sm">{displayName(s)}</span>
              <span className="text-[10px] font-mono text-muted-foreground ml-auto">{s.code}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
